import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import Async from 'components/Async';
import DatePicker from 'components/DatePicker';
import DaysCalendar from 'components/DaysCalendar';
import Page from 'components/Page';
import ToolTip from 'components/Placeholder/ToolTip';
import { DateTime, IntervalObject } from 'luxon';
import store from 'store';

import useDateQuery from '@common/helpers/useDateQuery';
import useModel from '@common/helpers/useModel';
import LocationMeet from '@common/models/Locations/LocationMeet';
import ReworkingMeetsModel, {
	IReworkingMeetsModel,
} from '@common/models/Reworkings/ReworkingMeets';
import { IReworkingSlotModel } from '@common/models/Reworkings/ReworkingSlot';
import ReworkingSlotsModel, {
	IReworkingSlotsModel,
} from '@common/models/Reworkings/ReworkingSlots';

import AppointmentsVisualGuide from '../../VisualGuide';
import { StyledAppointmentsHourWrap, StyledAppointmentsPageInnerWrap } from '../styled';
import AppointmentsBookingItem from './BookingItem';
import AppointmentsSelectPart from './SelectPart';
import {
	ADDITIONAL_INFO_SECTION,
	INFO_SECTION,
	STEP_SECTION,
	VISUAL_GUIDE_STEPS,
} from './constants';

type TFilters = {
	location_id?: number;
	group_id?: number;
	teacher_id?: number;
};

function AppointmentsPage() {
	const { t } = useTranslation();
	const { date, dateTime, setDateTime } = useDateQuery();

	const defaultLocation = store.profile.serviceLocations[0];
	const [filters, setFilters] = useState<TFilters>({
		location_id: defaultLocation?.id,
		group_id: store.profile.student?.groups.filter(
			(group: any) => group.location.id === defaultLocation?.id,
		)[0]?.id,
	});

	const locationMeet = useModel(
		LocationMeet,
		{
			id: filters.location_id,
		},
		[filters.location_id],
		{
			disableFetch: true,
		},
	);

	useEffect(() => {
		locationMeet.fetch({
			type: 'working_off',
		});
	}, [filters.location_id]);

	const teacher = locationMeet.teachers.find((teacher) => teacher.id === filters.teacher_id);

	const reworkingSlots = useModel(
		ReworkingSlotsModel,
		{
			period_start: dateTime.startOf('week').toISODate(),
			period_end: dateTime.endOf('week').toISODate(),
			location_id: locationMeet.id,
			service_id: store.services.current.id,
			...(filters.teacher_id && { teacher_id: filters.teacher_id }),
			...(filters.group_id && { group_id: filters.group_id }),
			slotDuration: locationMeet.duration,
		},
		[locationMeet, date, filters.teacher_id],
		{ disableFetch: true },
	) as IReworkingSlotsModel;

	useEffect(() => {
		if (teacher && filters.group_id && locationMeet.id) {
			reworkingSlots.fetch({
				location_id: locationMeet.id,
			});
		}
	}, [locationMeet, date, filters.teacher_id, filters.group_id]);

	const reworkingMeets = useModel(
		ReworkingMeetsModel,
		{
			period_start: dateTime.startOf('week').toISODate(),
			period_end: dateTime.endOf('week').toISODate(),
			location_id: locationMeet.id,
			service_id: store.services.current.id,
			...(filters.teacher_id && { teacher_id: filters.teacher_id }),
			...(filters.group_id && { group_id: filters.group_id }),
			slotDuration: locationMeet.duration,
		},
		[locationMeet, date, filters.teacher_id],
	) as IReworkingMeetsModel;

	const renderHour = (hour: number, day: IntervalObject) => {
		const hourDateTime = DateTime.fromObject({ hour, minute: 0 });
		const slots = reworkingSlots.daysMap[day.start.weekday][hour];

		return (
			<StyledAppointmentsHourWrap>
				<Typography variant="caption2">{hourDateTime.toFormat('HH:mm')}</Typography>
				{/* eslint-disable-next-line no-confusing-arrow */}
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '4px' }}>
					{Object.values(slots).map((slot: IReworkingSlotModel) =>
						slot ? (
							<AppointmentsBookingItem
								filters={filters}
								key={slot.id}
								slot={slot}
								reworkingMeets={reworkingMeets}
								teacher={teacher}
							/>
						) : null,
					)}
				</Box>
			</StyledAppointmentsHourWrap>
		);
	};

	return (
		<Page
			type="list"
			title={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{t('appointment.main')}
					<ToolTip title={t('appointment.timeZone')}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<InfoOutlinedIcon />
						</Box>
					</ToolTip>
				</Box>
			}
			sx={{ overflow: 'hidden' }}
			header={<DatePicker format="day" value={dateTime} onChange={setDateTime} />}
		>
			<StyledAppointmentsPageInnerWrap>
				<AppointmentsSelectPart
					filters={filters}
					locationMeet={locationMeet}
					onSubmit={setFilters}
				/>
				{teacher && filters.group_id ? (
					<Async loading={reworkingSlots.loading || reworkingMeets.loading}>
						<DaysCalendar
							days={reworkingSlots.days}
							date={date}
							dateTime={dateTime}
							renderHour={renderHour}
							minHour={10}
							maxHour={20}
						/>
					</Async>
				) : (
					<AppointmentsVisualGuide
						infoSection={INFO_SECTION}
						bookingSteps={STEP_SECTION}
						visualGuideSteps={VISUAL_GUIDE_STEPS}
						additionalInfoSection={ADDITIONAL_INFO_SECTION}
					/>
				)}
			</StyledAppointmentsPageInnerWrap>
		</Page>
	);
}

export default observer(AppointmentsPage);
